// Spreading a futures position across the budget months it actually covers.
//
// Futures trade in a handful of months - corn in March, May, July, September and December
// - while budgets are monthly. A position bought on the March contract to cover December,
// January and February budget arrives with its budget date set to March, so the dashboard
// showed the whole position against March and nothing against the months it was really
// bought for. Every summary card but one read as unhedged, and March read as over-hedged.
//
// This splits that volume across the budget months the commodity's contract-month mapping
// says price against that contract, weighted by each month's budget volume - a month
// budgeted for twice as much takes twice the futures.
//
// Replaces a hardcoded corn-only table. The mapping now comes from commodity settings, so
// a commodity whose contract months differ from corn's is handled by configuring it rather
// than by editing this file.
import {
    BudgetModel,
    ContractDate,
    PositionModel,
} from '../Generated/Commodity-Management-Api';
import { CommodityContractMonthMappingModel } from '../Apis/ContractMonthMappingApi';
import {
    getBudgetMonthsForContractDate,
    getMappingsForCommodity,
    BudgetMonthForContract,
} from './ContractMonthMappingHelper';
import { numberToShortMonth } from './MonthHelper';

/**
 * Only a position still sitting on its own contract month is a candidate.
 *
 * A futures position whose budget date has been set to something else has been assigned
 * deliberately - by an EFP posting, or by hand - and splitting it would undo that. Equal
 * dates are the signal that nobody has said which budget month it belongs to.
 */
function isUnassigned(position: PositionModel): boolean {
    return position.budgetDate?.month === position.contractDate?.month
        && position.budgetDate?.year === position.contractDate?.year;
}

/**
 * Whether a position can hedge a budget, location-wise.
 *
 * Futures are routinely booked a tier above the budgets they cover - the dashboard picks
 * up the selected location's parent for exactly that reason - so comparing location ids
 * for equality finds no budgets at all for those positions, and nothing ever splits. A
 * position covers a budget when it sits AT or ABOVE the budget's location, which the
 * budget's own hierarchy answers.
 *
 * Equality is still checked first, so this does not depend on whether the hierarchy
 * includes the location itself.
 */
function coversLocation(position: PositionModel, budget: BudgetModel): boolean {
    const positionLocationId = position.location?.locationId;
    if (positionLocationId === undefined) return false;

    if (budget.location?.locationId === positionLocationId) return true;

    return (budget.locationHierarchy ?? []).some(
        ancestor => ancestor.locationId === positionLocationId);
}

function budgetDateFor(
    target: BudgetMonthForContract,
    budget: BudgetModel | undefined,
): ContractDate {
    return {
        month: target.month,
        year: target.year,
        // The budget's own label where there is one: the dashboard groups on it, so an
        // invented label that formats differently would not match anything.
        label: budget?.budgetDate?.label ?? `${numberToShortMonth(target.month)} ${target.year}`,
    };
}

/**
 * Futures positions with contract-month volume spread over the budget months it covers.
 *
 * Positions that cannot be split are returned untouched, which is every case where
 * splitting would be a guess:
 *
 *   - the commodity has no contract-month mapping (contract month = budget month already)
 *   - the position's budget date was set to something other than its contract month
 *   - nothing in the mapping points at this contract month
 *   - none of the covered months has a budget, or they total zero volume
 *
 * Volume is conserved. Weights are taken over the months that actually have budgets, so
 * the shares sum to one even when a covered month has no budget - otherwise the missing
 * month's share would simply vanish from the dashboard.
 */
export function splitFuturesAcrossBudgetMonths(
    futures: readonly PositionModel[],
    budgets: readonly BudgetModel[],
    mappings: readonly CommodityContractMonthMappingModel[] | undefined,
): PositionModel[] {
    const result: PositionModel[] = [];

    for (const future of futures) {
        if (!isUnassigned(future)) {
            result.push(future);
            continue;
        }

        const mappingsByBudgetMonth = getMappingsForCommodity(
            mappings, future.commodity?.commodityId);

        if (Object.keys(mappingsByBudgetMonth).length === 0) {
            // Unmapped commodity: contract month IS the budget month, nothing to spread.
            result.push(future);
            continue;
        }

        const targets = getBudgetMonthsForContractDate(
            future.contractDate?.month, future.contractDate?.year, mappingsByBudgetMonth);

        if (targets.length === 0) {
            // Mapped, but nothing points at this contract month - the mapping has no
            // opinion, so leave the position where it is rather than invent a month.
            result.push(future);
            continue;
        }

        // Matched on commodity and location: a futures position must not be weighted by
        // a budget it has nothing to do with.
        const covering = targets.map(target => ({
            target,
            budget: budgets.find(budget =>
                budget.commodity?.commodityId === future.commodity?.commodityId
                && coversLocation(future, budget)
                && budget.budgetDate?.month === target.month
                && budget.budgetDate?.year === target.year),
        }));

        const totalBudgetVolume = covering.reduce(
            (total, entry) => total + (entry.budget?.volume ?? 0), 0);

        if (totalBudgetVolume === 0) {
            // None of the months this contract covers is budgeted at this location - a
            // September corn contract covers July and August, and a plant whose budget
            // starts in September has neither.
            //
            // Deliberately dropped rather than left on its own contract month. Keeping it
            // parked September's whole hedge onto the September card, which the mapping
            // says that contract does not cover at all, and read as 712% hedged. The
            // position is real and still on the positions tab; it just has no budget
            // month here to sit against.
            continue;
        }

        for (const entry of covering) {
            const budgetVolume = entry.budget?.volume ?? 0;
            if (budgetVolume === 0) continue;

            result.push({
                ...future,
                budgetDate: budgetDateFor(entry.target, entry.budget),
                volume: (future.volume ?? 0) * (budgetVolume / totalBudgetVolume),
            });
        }
    }

    return result;
}
